import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import Logger from "../../../services/Logger";
import AddBannedWordDialog from "./AddBannedWordDialog";

function formatCategoryLabel(category) {
    if (!category) {
        return "";
    }
    return category
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

export default function BannedDictionaryPanel({ language, bannedWords, onAdd }) {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [result, setResult] = useState(null);
    const [openAdd, setOpenAdd] = useState(false);

    const search = async () => {
        const word = query.trim();
        if (!word) {
            return;
        }
        setLoading(true);
        setSearched(true);
        try {
            const rows = await DictionaryApiService.getWordDefinition(word, language);
            const hit = Array.isArray(rows) ? rows[0] : null;
            if (hit?.word) {
                setResult({
                    word: hit.word,
                    meanings: hit.meanings || [],
                    found: true,
                });
            } else {
                setResult({ word, meanings: [], found: false });
            }
        } catch (error) {
            Logger.error("Dictionary search failed:", error);
            setResult({ word, meanings: [], found: false });
        } finally {
            setLoading(false);
        }
    };

    const existing = result
        ? (bannedWords || []).find((item) =>
            String(item.word || "").toLowerCase() === String(result.word || "").toLowerCase()
            && (!item.language || item.language === language))
        : null;

    const handleAdd = async (word, category, lang) => {
        const ok = await onAdd(word, category, lang);
        if (ok) {
            setOpenAdd(false);
        }
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search the {language} dictionary, then add the word to the banned list.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, maxWidth: 560 }}>
                <TextField
                    size="small"
                    fullWidth
                    label="Word"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            search();
                        }
                    }}
                />
                <Button variant="contained" onClick={search} disabled={loading || !query.trim()}>
                    Search
                </Button>
            </Box>
            {loading ? <CircularProgress size={24} /> : null}
            {!loading && searched && result ? (
                <Paper variant="outlined" sx={{ p: 2, maxWidth: 720 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6">{result.word}</Typography>
                        {existing ? (
                            <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={`Banned · ${formatCategoryLabel(existing.category)}`}
                            />
                        ) : null}
                        {!result.found ? (
                            <Chip size="small" label="Not in dictionary" variant="outlined" />
                        ) : null}
                    </Box>
                    {result.found && result.meanings.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No definition available for this word.
                        </Typography>
                    ) : null}
                    {result.meanings.map((meaning, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ textTransform: "capitalize", mb: 0.5 }}>
                                {meaning.partOfSpeech}
                            </Typography>
                            {(meaning.definitions || []).map((def, defIndex) => (
                                <Typography key={defIndex} variant="body2" sx={{ pl: 1, mb: 0.5 }}>
                                    {def.definition}
                                </Typography>
                            ))}
                        </Box>
                    ))}
                    {!result.found ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            This word is not in the dictionary. You can still add it to the banned list.
                        </Typography>
                    ) : null}
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={Boolean(existing)}
                        onClick={() => setOpenAdd(true)}
                    >
                        {existing ? "Already banned" : "Add to banned words"}
                    </Button>
                </Paper>
            ) : null}
            <AddBannedWordDialog
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                onAdd={handleAdd}
                selectedWord={result?.word || ""}
                language={language}
            />
        </Box>
    );
}
